/**
 * Dashboard Layout Component
 * Provides consistent layout structure for all dashboard pages
 * with Sidebar and header
 */

import { formatShortDate } from '@/renderer/lib/utils';
import { AppDispatch } from '@/renderer/store';
import {
  selectBillingRecoveryDates,
  selectCanOpenDashboardShell,
  selectCurrentUser,
  selectIsSubscriptionInGracePeriod,
  selectIsSubscriptionSuspended,
} from '@/renderer/store/auth/authSelectors';
import { selectNotificationCount } from '@/renderer/store/globalFilters';
import { fetchOutletsWithStatsThunk } from '@/renderer/store/outlets';
import { Bell } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { BILLING_PAGE_PATH, pageTitles } from '../../util/renderConstants';
import { OutletSelector, SyncStatusChip } from '../common';
import { AlertCard } from '../common/AlertCard';
import { UserActionsMenu } from '../nav/UserActionsMenu';
import { Sidebar } from './superuser/Sidebar';

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const currentUser = useSelector(selectCurrentUser);
  const canOpenDashboardShell = useSelector(selectCanOpenDashboardShell);
  const isSubscriptionInGracePeriod = useSelector(
    selectIsSubscriptionInGracePeriod,
  );
  const isSubscriptionSuspended = useSelector(selectIsSubscriptionSuspended);
  const billingRecoveryDates = useSelector(selectBillingRecoveryDates);
  const notificationCount = useSelector(selectNotificationCount);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const shouldLockDashboardContent = isSubscriptionSuspended;

  const currentPage = pageTitles[location.pathname] || {
    title: 'Dashboard',
    subtitle: 'Your business dashboard',
  };

  // Fetch outlets for the company on mount
  useEffect(() => {
    if (currentUser?.company?.id) {
      dispatch(fetchOutletsWithStatsThunk(currentUser.company.id));
    }
  }, [dispatch, currentUser?.company?.id]);

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  if (!canOpenDashboardShell) {
    return <Navigate to="/sales" replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-sans text-sm text-gray-900">
      {/* Sidebar */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {currentPage.title}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{currentPage.subtitle}</p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Sync Status */}
            <SyncStatusChip />

            {/* Outlet Selector */}
            <OutletSelector />

            {/* Notifications */}
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors relative"
            >
              <Bell size={22} />
              {notificationCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center px-1">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </button>

            <UserActionsMenu />
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="flex-1 overflow-y-auto p-8">
          <div className="max-w-7xl mx-auto space-y-4">
            {isSubscriptionInGracePeriod ? (
              <AlertCard
                title="Billing grace period active"
                description={`Payment is still pending for this workspace. Please renew before ${billingRecoveryDates.gracePeriodEndsAt ? formatShortDate(billingRecoveryDates.gracePeriodEndsAt) : 'the grace window ends'} to avoid service interruption.`}
                buttonLink={BILLING_PAGE_PATH}
                buttonText="Review billing"
              />
            ) : null}

            {shouldLockDashboardContent ? (
              <AlertCard
                title="Subscription suspended until billing is restored"
                description="This workspace is currently in billing recovery mode. The dashboard remains visible for reference, but changes are locked until the subscription is renewed."
                info={`Data retention is scheduled until ${billingRecoveryDates.dataPurgeScheduledFor ? formatShortDate(billingRecoveryDates.dataPurgeScheduledFor) : ' a later recovery date'}`}
                buttonLink={BILLING_PAGE_PATH}
                buttonText="Restore billing"
              />
            ) : null}

            <div
              className={
                shouldLockDashboardContent
                  ? 'pointer-events-none opacity-45 saturate-50'
                  : ''
              }
            >
              {children || <Outlet />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
